import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import { api, fmtDateTime } from "../lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

export function NotificationsMenu() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items);
      setUnread(data.unread);
    } catch {}
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const openItem = async (n) => {
    setOpen(false);
    try {
      await api.post(`/notifications/${n.id}/read`);
    } catch {}
    setUnread((u) => Math.max(0, u - (n.read ? 0 : 1)));
    if (n.link) navigate(n.link);
  };

  const readAll = async () => {
    try {
      await api.post("/notifications/read-all");
      setUnread(0);
      setItems((items) => items.map((i) => ({ ...i, read: true })));
    } catch {}
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="notifications-bell"
          className="relative flex h-10 w-10 items-center justify-center rounded-xl border border-[#1D225C]/10 bg-white shadow-sm transition-colors hover:border-[#B88A4A]/50"
        >
          <Bell className="h-[18px] w-[18px] text-[#1D225C]" />
          {unread > 0 && (
            <span
              data-testid="notifications-badge"
              className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-[#B88A4A] px-1 text-[10px] font-bold text-white"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 rounded-2xl p-0" data-testid="notifications-panel">
        <div className="flex items-center justify-between border-b border-[#1D225C]/10 px-4 py-3">
          <p className="font-display text-sm font-semibold text-[#1D225C]">Benachrichtigungen</p>
          {unread > 0 && (
            <button
              data-testid="notifications-read-all"
              onClick={readAll}
              className="flex items-center gap-1 text-xs font-medium text-[#B88A4A] hover:underline"
            >
              <CheckCheck className="h-3.5 w-3.5" /> Alle gelesen
            </button>
          )}
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {items.length === 0 && (
            <p className="px-4 py-8 text-center text-sm text-[#777]" data-testid="notifications-empty">
              Keine Benachrichtigungen.
            </p>
          )}
          {items.map((n) => (
            <button
              key={n.id}
              data-testid={`notification-item-${n.id}`}
              onClick={() => openItem(n)}
              className={`flex w-full gap-3 border-b border-[#1D225C]/5 px-4 py-3 text-left transition-colors hover:bg-[#B88A4A]/5 ${
                n.read ? "opacity-60" : ""
              }`}
            >
              <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${n.read ? "bg-transparent" : "bg-[#B88A4A]"}`} />
              <span className="min-w-0">
                <span className="block text-sm leading-snug text-[#1D225C]">{n.message}</span>
                <span className="mt-0.5 block text-xs text-[#777]">{fmtDateTime(n.created_at)}</span>
              </span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
